import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { CAMPUS_NEWS, ANNOUNCEMENTS } from '../graphql';
import { useAuth } from '../context/AuthContext';
import {
  Newspaper, Megaphone, Pin, Info, BookOpen, Calendar, Trophy,
  Briefcase, Music, GraduationCap, Sun, Search, Filter,
  Building2, AlertTriangle, Eye, Clock, ChevronDown, ChevronUp,
  Bell,
} from 'lucide-react';

const NEWS_CATS = [
  { value: '',          label: 'All',        icon: Newspaper,     color: 'text-gray-600    bg-gray-100' },
  { value: 'GENERAL',   label: 'General',    icon: Info,          color: 'text-blue-600    bg-blue-100' },
  { value: 'ACADEMIC',  label: 'Academic',   icon: BookOpen,      color: 'text-brand-600   bg-brand-100' },
  { value: 'EVENTS',    label: 'Events',     icon: Calendar,      color: 'text-brand-700   bg-brand-50' },
  { value: 'SPORTS',    label: 'Sports',     icon: Trophy,        color: 'text-green-600   bg-green-100' },
  { value: 'PLACEMENT', label: 'Placement',  icon: Briefcase,     color: 'text-orange-600  bg-orange-100' },
  { value: 'CULTURAL',  label: 'Cultural',   icon: Music,         color: 'text-brand-600   bg-brand-100' },
  { value: 'EXAM',      label: 'Exam',       icon: GraduationCap, color: 'text-red-600     bg-red-100' },
  { value: 'HOLIDAY',   label: 'Holiday',    icon: Sun,           color: 'text-yellow-600  bg-yellow-100' },
];

const ANN_TYPES = {
  GENERAL:      { label: 'General',      color: 'text-blue-700   bg-blue-100',   icon: Info },
  CANCELLATION: { label: 'Cancellation', color: 'text-red-700    bg-red-100',    icon: AlertTriangle },
  EXAM_CHANGE:  { label: 'Exam Change',  color: 'text-orange-700 bg-orange-100', icon: Calendar },
  HOLIDAY:      { label: 'Holiday',      color: 'text-green-700  bg-green-100',  icon: Sun },
  URGENT:       { label: 'Urgent',       color: 'text-red-800    bg-red-200',    icon: AlertTriangle },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)   return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)    return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function NewsCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = NEWS_CATS.find(c => c.value === item.category) || NEWS_CATS[1];
  const Icon = cfg.icon;

  return (
    <div className={`card overflow-hidden transition-shadow hover:shadow-md ${item.pinned ? 'ring-1 ring-teal-300' : ''}`}>
      {item.imageUrl && (
        <div className="w-full h-44 bg-gray-100 overflow-hidden">
          <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover"
            onError={e => { e.target.parentElement.style.display = 'none'; }} />
        </div>
      )}
      <div className="p-4">
        {item.pinned && (
          <div className="flex items-center gap-1 text-xs text-teal-600 font-medium mb-2">
            <Pin size={11}/> Pinned
          </div>
        )}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
            <Icon size={11}/>{cfg.label}
          </span>
          {item.branch && (
            <span className="text-xs text-gray-400 flex items-center gap-0.5">
              <Building2 size={10}/>{item.branch}
            </span>
          )}
        </div>
        <h3 className="font-semibold text-gray-900 leading-snug">{item.title}</h3>

        <p className={`text-sm text-gray-600 mt-1.5 ${expanded ? '' : 'line-clamp-3'}`}>{item.body}</p>
        {item.body.length > 180 && (
          <button onClick={() => setExpanded(e=>!e)} className="text-xs text-teal-600 hover:underline mt-1 flex items-center gap-0.5">
            {expanded ? <><ChevronUp size={12}/> Show less</> : <><ChevronDown size={12}/> Read more</>}
          </button>
        )}

        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.map(t => <span key={t} className="badge text-xs">#{t}</span>)}
          </div>
        )}

        <div className="flex items-center justify-between mt-3 pt-2 border-t text-xs text-gray-400">
          <div className="flex items-center gap-1">
            <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center">
              <Shield2 size={10} className="text-teal-600" />
            </div>
            <span>{item.admin?.name}</span>
            {item.admin?.designation && <span className="text-gray-300">• {item.admin.designation}</span>}
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-0.5"><Eye size={11}/>{item.views}</span>
            <span className="flex items-center gap-0.5"><Clock size={11}/>{timeAgo(item.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Use a shield icon inline
function Shield2({ size, className }) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
}

function AnnouncementCard({ ann }) {
  const tc = ANN_TYPES[ann.type] || ANN_TYPES.GENERAL;
  const Icon = tc.icon;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`card p-4 ${ann.pinned ? 'ring-1 ring-orange-300' : ''}`}>
      {ann.pinned && <div className="flex items-center gap-1 text-xs text-orange-600 font-medium mb-1.5"><Pin size={11}/> Pinned</div>}
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl flex-none ${tc.color.split(' ').slice(1).join(' ')}`}>
          <Icon size={18} className={tc.color.split(' ')[0]} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tc.color}`}>{tc.label}</span>
            {ann.branch && <span className="text-xs text-gray-400">{ann.branch}</span>}
            {ann.subject && <span className="text-xs text-gray-400">• {ann.subject}</span>}
          </div>
          <h3 className="font-semibold text-sm text-gray-900">{ann.title}</h3>
          <p className={`text-xs text-gray-600 mt-0.5 ${expanded ? '' : 'line-clamp-2'}`}>{ann.body}</p>
          {ann.body.length > 120 && (
            <button onClick={() => setExpanded(e=>!e)} className="text-xs text-teal-600 hover:underline mt-0.5">
              {expanded ? 'Less' : 'More'}
            </button>
          )}
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
            <span>{ann.author?.name}</span>
            <span>{timeAgo(ann.createdAt)}</span>
            {ann.views > 0 && <span className="flex items-center gap-0.5"><Eye size={10}/>{ann.views}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CampusNewsPage() {
  const { user } = useAuth();
  const [tab, setTab]           = useState('news');
  const [category, setCategory] = useState('');
  const [search, setSearch]     = useState('');
  const [myCollegeOnly, setMyCollegeOnly] = useState(true);

  const college = myCollegeOnly ? (user?.college || undefined) : undefined;
  const branch  = myCollegeOnly ? (user?.branch  || undefined) : undefined;

  const { data: newsData, loading: nl } = useQuery(CAMPUS_NEWS, {
    variables: { college, branch, category: category||undefined, search: search||undefined },
    fetchPolicy: 'cache-and-network',
  });

  const { data: annData, loading: al } = useQuery(ANNOUNCEMENTS, {
    variables: { college, branch },
    fetchPolicy: 'cache-and-network',
  });

  const news = newsData?.campusNews   || [];
  const anns = annData?.announcements?.filter(a => a.author?.id?.startsWith('admin_')) || [];

  const pinnedNews = news.filter(n => n.pinned);
  const regularNews = news.filter(n => !n.pinned);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Newspaper className="text-teal-600" size={26}/> Campus News
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Official news &amp; announcements from your college admins</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={myCollegeOnly} onChange={e => setMyCollegeOnly(e.target.checked)} className="rounded text-teal-600" />
          <span className="text-sm text-gray-600">My college only</span>
        </label>
      </div>

      {/* College scope badge */}
      {myCollegeOnly && user?.college && (
        <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-200 rounded-xl text-xs text-teal-700">
          <Building2 size={14}/>
          <span>Showing news for: <strong>{user.branch ? `${user.branch} — ` : ''}{user.college}</strong></span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { id: 'news', label: 'News Feed',      icon: Newspaper, count: news.length },
          { id: 'ann',  label: 'Announcements',  icon: Megaphone, count: anns.length },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab===t.id ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={15}/>{t.label}
            {t.count > 0 && <span className={`text-xs rounded-full px-1.5 ${tab===t.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'news' && (
        <>
          {/* Search + category filter */}
          <div className="card p-3 space-y-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="input pl-9 py-2 text-sm" placeholder="Search news…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {NEWS_CATS.map(c => {
                const Icon = c.icon;
                return (
                  <button key={c.value} onClick={() => setCategory(c.value === category ? '' : c.value)}
                    className={`flex-none flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${category===c.value ? c.color+' border-current' : 'text-gray-500 border-gray-200 hover:border-gray-400'}`}>
                    <Icon size={11}/>{c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {nl ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(4)].map((_,i) => <div key={i} className="card p-4 animate-pulse h-48"><div className="h-28 bg-gray-100 rounded-lg mb-3"/><div className="h-4 bg-gray-200 rounded w-3/4"/></div>)}
            </div>
          ) : news.length === 0 ? (
            <div className="card p-12 text-center">
              <Newspaper size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500">No news found</p>
              {myCollegeOnly && !user?.college && <p className="text-gray-400 text-sm mt-1">Update your college in profile to see scoped news</p>}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Pinned */}
              {pinnedNews.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-2 flex items-center gap-1"><Pin size={11}/> Pinned</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pinnedNews.map(n => <NewsCard key={n.id} item={n} />)}
                  </div>
                </div>
              )}
              {/* Regular */}
              {regularNews.length > 0 && (
                <div>
                  {pinnedNews.length > 0 && <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Latest</p>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {regularNews.map(n => <NewsCard key={n.id} item={n} />)}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'ann' && (
        <div className="space-y-2">
          {al ? (
            [...Array(3)].map((_,i) => <div key={i} className="card p-4 animate-pulse h-20"><div className="h-4 bg-gray-200 rounded w-3/4"/></div>)
          ) : anns.length === 0 ? (
            <div className="card p-12 text-center">
              <Megaphone size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500">No admin announcements</p>
            </div>
          ) : anns.map(a => <AnnouncementCard key={a.id} ann={a} />)}
        </div>
      )}
    </div>
  );
}
